import type { SellerCollectsConfig } from '@b-stock/fe-core/bstock-next/queries'
import { StoreFrontOptionsSellerCollectsDtoShippingArrangedByEnum as ShippingArrangedBy } from '@b-stock/listing-api-client'

import type { SaleKind } from '@/buyer-portal/helpers/sellerCollectsSaleKind'

export const SELLER_COLLECTS_NS = 'BidConfirmationModal.sellerCollects'

/**
 * Buy Now reuses this whole copy block, and almost all of it was written for
 * an auction. "If you win" in front of a purchase that has already happened
 * makes a settled obligation sound conditional, so the immediate sale gets its
 * own wording wherever the tense actually differs.
 *
 * Re-exported rather than declared: the type is shared with surfaces that have
 * no business importing from a modal.
 */
export type { SaleKind }

/**
 * The keys that read differently on an immediate sale. Anything not listed
 * says the same thing either way and has one string.
 *
 * Falling back to the bid wording for an unlisted key is deliberate: a missing
 * purchase variant then reads oddly rather than rendering a raw message id.
 */
const PURCHASE_VARIANT_KEYS = new Set([
  'total.maxBid',
  'total.deadline',
  'total.toBStockDescription',
  'cardPromptBody',
  'termsAcknowledgement',
])

/** The message id for a Seller Collects key in the tense of this sale. */
export const sellerCollectsCopyId = (
  key: string,
  saleKind: SaleKind = 'bid'
): string =>
  saleKind === 'purchase' && PURCHASE_VARIANT_KEYS.has(key)
    ? `${SELLER_COLLECTS_NS}.purchase.${key}`
    : `${SELLER_COLLECTS_NS}.${key}`

/** Exported for the test that proves every variant exists in the locale. */
export const purchaseVariantKeys = (): string[] => [...PURCHASE_VARIANT_KEYS]

/**
 * B-Stock only quotes freight it books itself. Every other arrangement is
 * settled between buyer and seller, so the modal must not show an amount.
 */
export const bStockQuotesShipping = (
  config: SellerCollectsConfig | undefined
): boolean =>
  config?.shippingArrangedBy === ShippingArrangedBy.BookedThroughBstock

/**
 * The seller's payment deadline, in business days. Undefined where the
 * storefront has not set one — the modal then asserts no deadline rather
 * than inventing a default.
 */
export const getPaymentDeadlineDays = (
  config: SellerCollectsConfig | undefined
): number | undefined => {
  const days = config?.paymentDueBusinessDays
  return typeof days === 'number' && days > 0 ? days : undefined
}
